import React, { useState, useEffect, useCallback, useRef } from 'react';
import './OneLiner.style.scss';
import { usePlayer } from '../../../../Contexts/playerData.context.jsx';
// eslint-disable-next-line
import { saveToDB, getNipList, getUserFromToken } from '../../../../utils/functions.ts';
// eslint-disable-next-line
import checkIcon from '../../../../assets/icons/QuickList.svg';
import checkIconOff from '../../../../assets/icons/NoQuickList.svg';
import { useDataLayer } from '../../../../Contexts/DataLayer.context.jsx';
import AddToBox from '../../../Boxes/AddToBox/addToBox.component.jsx';
import { FindCFLTeam } from '../../../../utils/loadFunctions.ts';
import { FindStatus } from '../../../../utils/loadFunctions.ts';
import { useAllThingsLists } from '../../../../Contexts/allThingsList.context.jsx';
import { useNavBar } from '../../../../Contexts/navbar.items.context.jsx';
import { useMask } from '../../../../Contexts/mask.context.jsx';

const AUTO_SCROLL_STORAGE_KEY = "FBX_ONE_LINER_AUTO_SCROLL";
const AUTO_SCROLL_EVENT = "FBX_ONE_LINER_AUTO_SCROLL_CHANGE";
const SHOW_SCROLLBARS_STORAGE_KEY = "FBX_ONE_LINER_SHOW_SCROLLBARS";
const SHOW_SCROLLBARS_EVENT = "FBX_ONE_LINER_SHOW_SCROLLBARS_CHANGE";
const PLAYER_GRADE_UPDATE_EVENT = "FBX_PLAYER_GRADE_UPDATE";
const PLAYER_ROW_UPDATE_EVENT = "FBX_PLAYER_ROW_UPDATE";
const DOUBLE_TAP_MS = 450;

const hasBluBarsAccess = (user) => {
    const securityLevel = String(user?.userContactLevel ?? user?.security ?? user?.ADMIN ?? user?.admin ?? '').trim();
    return securityLevel === '1' || user?.ADMIN === true || user?.admin === true;
};

const OneLiner = () => {
    const { listArray, updateListArray } = useDataLayer();
    const { quickList, quickListNumbers, updateQuickListNumbers, updateQuickList } = useAllThingsLists();
    const {
        playerID,
        playerData,
        setPlayerData,
        listLoadID,
        setPlayerID,
        activePlayer,
        setActivePlayer
    } = usePlayer();

    const { updateWhichOneLine, activeSideButton, updateActiveSideButton } = useNavBar();
    const { showMask } = useMask();

    const [sortField, setSortField] = useState('last');
    const [sortOrder, setSortOrder] = useState('asc');
    const [clickTimeout, setClickTimeout] = useState(null);
    const [rapidListID, setRapidListID] = useState(null);
    const [displayData, setDisplayData] = useState([]);
    const [boxPosition, setBoxPosition] = useState({ top: 0, left: 0 });
    const [isAddOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const lastListLoadIDRef = useRef(null);
    const lastTapRef = useRef({ playerID: '', time: 0 });
    const [canOpenBluBars, setCanOpenBluBars] = useState(false);
    const [showScrollbars, setShowScrollbars] = useState(() => {
        try {
            return localStorage.getItem(SHOW_SCROLLBARS_STORAGE_KEY) === "On";
        } catch {
            return false;
        }
    });
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(() => {
        try {
            return localStorage.getItem(AUTO_SCROLL_STORAGE_KEY) !== "Off";
        } catch {
            return true;
        }
    });

    const normalizeString = (value) => (value ?? '').toString().trim().toLowerCase();

    const sortPlayers = useCallback((data, field, order = 'asc') => {
        const sorted = [...(Array.isArray(data) ? data : [])].sort((a, b) => {
            let aValue;
            let bValue;

            switch (field) {
                case 'height':
                case 'weight':
                case 'speed40Fastest':
                case 'optionYr':
                case 'age':
                case 'jersey':
                case 'proJersey':
                    aValue =
                        isNaN(Number(a?.[field])) || ['0', ' ', null, '', '-'].includes(a?.[field])
                            ? Infinity
                            : Number(a?.[field]);
                    bValue =
                        isNaN(Number(b?.[field])) || ['0', ' ', null, '', '-'].includes(b?.[field])
                            ? Infinity
                            : Number(b?.[field]);
                    break;

                case 'nflDate':
                case 'cflDate':
                    aValue = [' ', null, '', '-', '0000-00-00'].includes(a?.[field]) ? '' : (a?.[field] ?? '');
                    bValue = [' ', null, '', '-', '0000-00-00'].includes(b?.[field]) ? '' : (b?.[field] ?? '');
                    break;

                case 'report':
                    aValue = [' ', null, '', '-'].includes(a?.[field]) ? Infinity : Number(a?.[field] ?? 0);
                    bValue = [' ', null, '', '-'].includes(b?.[field]) ? Infinity : Number(b?.[field] ?? 0);
                    break;

                case 'cflTeam':
                case 'nflTeam':
                case 'theEvent':
                case 'school':
                case 'genPos':
                case 'ufl24Team':
                    aValue = [' ', null, '', '-'].includes(a?.[field]) ? '' : normalizeString(a?.[field]);
                    bValue = [' ', null, '', '-'].includes(b?.[field]) ? '' : normalizeString(b?.[field]);
                    break;

                case 'finalOut':
                case 'masterGrade':
                case 'agentNum':
                    aValue =
                        isNaN(Number(a?.[field])) || ['0', ' ', null, '', '-'].includes(a?.[field])
                            ? 0
                            : Number(a?.[field]);
                    bValue =
                        isNaN(Number(b?.[field])) || ['0', ' ', null, '', '-'].includes(b?.[field])
                            ? 0
                            : Number(b?.[field]);
                    break;

                case 'last': {
                    const lastCompare = normalizeString(a?.last).localeCompare(normalizeString(b?.last));
                    if (lastCompare !== 0) {
                        return order === 'desc' ? -lastCompare : lastCompare;
                    }

                    const firstCompare = normalizeString(a?.first).localeCompare(normalizeString(b?.first));
                    return order === 'desc' ? -firstCompare : firstCompare;
                }

                default:
                    aValue = normalizeString(a?.[field]);
                    bValue = normalizeString(b?.[field]);
                    break;
            }

            if (aValue < bValue) return order === 'desc' ? 1 : -1;
            if (aValue > bValue) return order === 'desc' ? -1 : 1;
            return 0;
        });

        return sorted;
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadBluBarsAccess = async () => {
            const user = await getUserFromToken();
            if (!isMounted) return;
            setCanOpenBluBars(hasBluBarsAccess(user));
        };

        loadBluBarsAccess();

        const handleFocus = () => loadBluBarsAccess();
        window.addEventListener('focus', handleFocus);

        return () => {
            isMounted = false;
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    const handleCheckboxChange = (FBXID) => {
        if (clickTimeout) {
            clearTimeout(clickTimeout);
            setClickTimeout(null);
        } else {
            const timeout = setTimeout(() => {
                setClickTimeout(null);
            }, 100);
            setClickTimeout(timeout);

            const newNumberArray = quickListNumbers.includes(FBXID)
                ? quickListNumbers.filter(item => item !== FBXID)
                : [...quickListNumbers, FBXID];

            updateQuickListNumbers(newNumberArray);
            quickList.theNumbers = newNumberArray;
            quickList.listTotal = newNumberArray.length;
            updateQuickList(quickList);
            saveToDB(FBXID, quickList.listId);

            const updatedListArray = listArray.map(item => {
                if (item.listId === quickList.listId) {
                    return { ...item, ...quickList };
                }
                return item;
            });
            updateListArray(updatedListArray);
        }
    };

    // when a NEW list loads, default sort alpha last, first
    useEffect(() => {
        if (!listLoadID) return;
        if (!Array.isArray(playerData)) return;
        if (lastListLoadIDRef.current === listLoadID) return;
        lastListLoadIDRef.current = listLoadID;

        const sortedPlayers = sortPlayers(playerData, 'last', 'asc');
        setDisplayData(sortedPlayers);
        setSortField('last');
        setSortOrder('asc');
        setSelectedIndex(0);
    }, [listLoadID, playerData, sortPlayers]);

    // whenever playerData changes, keep the visible list in sync using the current sort
    useEffect(() => {
        if (!Array.isArray(playerData)) {
            setDisplayData([]);
            return;
        }

        const sortedPlayers = sortPlayers(playerData, sortField, sortOrder);
        setDisplayData(sortedPlayers);
    }, [playerData, sortField, sortOrder, sortPlayers]);

    useEffect(() => {
        if (!playerID) return;
        if (!Array.isArray(playerData) || playerData.length === 0) return;
        if (typeof setActivePlayer !== "function") return;

        const activeData = playerData.find(
            (item) => String(item?.FBXID) === String(playerID)
        );

        if (
            activeData &&
            String(activePlayer?.FBXID || "") !== String(activeData.FBXID)
        ) {
            setActivePlayer(activeData);
        }
    }, [playerID, playerData, activePlayer?.FBXID, setActivePlayer]);

    useEffect(() => {
        const readAutoScrollSetting = () => {
            try {
                setAutoScrollEnabled(localStorage.getItem(AUTO_SCROLL_STORAGE_KEY) !== "Off");
            } catch {
                setAutoScrollEnabled(true);
            }
        };

        window.addEventListener(AUTO_SCROLL_EVENT, readAutoScrollSetting);
        window.addEventListener("storage", readAutoScrollSetting);

        return () => {
            window.removeEventListener(AUTO_SCROLL_EVENT, readAutoScrollSetting);
            window.removeEventListener("storage", readAutoScrollSetting);
        };
    }, []);

    useEffect(() => {
        const readScrollbarSetting = () => {
            try {
                setShowScrollbars(localStorage.getItem(SHOW_SCROLLBARS_STORAGE_KEY) === "On");
            } catch {
                setShowScrollbars(false);
            }
        };

        window.addEventListener(SHOW_SCROLLBARS_EVENT, readScrollbarSetting);
        window.addEventListener("storage", readScrollbarSetting);

        return () => {
            window.removeEventListener(SHOW_SCROLLBARS_EVENT, readScrollbarSetting);
            window.removeEventListener("storage", readScrollbarSetting);
        };
    }, []);

    useEffect(() => {
        const activeList = Array.isArray(displayData) ? displayData : [];

        if (activeList.length === 0) {
            setSelectedIndex(0);
            return;
        }

        const activeFBXID = playerID || activePlayer?.FBXID;
        const refIndex = activeList.findIndex((item) => String(item?.FBXID) === String(activeFBXID));
        if (refIndex >= 0) {
            setSelectedIndex((prev) => (prev === refIndex ? prev : refIndex));
            return;
        }

        if (selectedIndex < 0 || selectedIndex >= activeList.length) {
            setSelectedIndex(0);
        }
    }, [displayData, playerID, activePlayer?.FBXID, selectedIndex]);

    useEffect(() => {
        if (!autoScrollEnabled) return;
        if (selectedIndex < 0) return;

        requestAnimationFrame(() => {
            const container = document.querySelector(".one-liner-container");
            const row = container?.querySelector(`[data-rowindex="${selectedIndex}"]`);
            row?.scrollIntoView({ block: "center" });
        });
    }, [autoScrollEnabled, selectedIndex, displayData]);

    useEffect(() => {
        const handlePlayerGradeUpdate = (event) => {
            const { FBXID, masterGrade } = event?.detail || {};
            if (!FBXID) return;

            const nextGrade =
                masterGrade === null || masterGrade === undefined || String(masterGrade).trim() === ""
                    ? "-"
                    : String(masterGrade);

            const patchArray = (prev) => {
                if (!Array.isArray(prev)) return prev;
                return prev.map((p) =>
                    String(p?.FBXID ?? p?.DR_Num ?? p?.DRNUM ?? p?.playerID ?? "") === String(FBXID)
                        ? { ...p, masterGrade: nextGrade }
                        : p
                );
            };

            setDisplayData(patchArray);
            if (typeof setPlayerData === "function") {
                setPlayerData(patchArray);
            }
        };

        window.addEventListener(PLAYER_GRADE_UPDATE_EVENT, handlePlayerGradeUpdate);
        return () => window.removeEventListener(PLAYER_GRADE_UPDATE_EVENT, handlePlayerGradeUpdate);
    }, [setPlayerData]);

    useEffect(() => {
        const handlePlayerRowUpdate = (event) => {
            const { playerRow } = event?.detail || {};
            if (!playerRow?.FBXID) return;

            const patchArray = (prev) => {
                if (!Array.isArray(prev)) return prev;
                return prev.map((p) =>
                    String(p?.FBXID ?? p?.DR_Num ?? p?.DRNUM ?? p?.playerID ?? "") === String(playerRow.FBXID)
                        ? { ...p, ...playerRow }
                        : p
                );
            };

            setDisplayData(patchArray);
            if (typeof setPlayerData === "function") {
                setPlayerData(patchArray);
            }
        };

        window.addEventListener(PLAYER_ROW_UPDATE_EVENT, handlePlayerRowUpdate);
        return () => window.removeEventListener(PLAYER_ROW_UPDATE_EVENT, handlePlayerRowUpdate);
    }, [setPlayerData]);

    useEffect(() => {
        const blockedButtons = new Set(['', 'notes', 'reports', 'reports1', 'Files']);

        const handleArrowNav = (e) => {
            if (showMask) return;
            if (blockedButtons.has(activeSideButton)) return;
            if (!Array.isArray(displayData) || displayData.length === 0) return;

            const tag = (e.target?.tagName || '').toLowerCase();
            const isTypingTarget =
                tag === 'input' ||
                tag === 'textarea' ||
                tag === 'select' ||
                e.target?.isContentEditable;

            if (isTypingTarget) return;
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

            e.preventDefault();

            let nextIndex = selectedIndex;

            if (e.key === 'ArrowDown') {
                nextIndex = Math.min(selectedIndex + 1, displayData.length - 1);
            } else {
                nextIndex = Math.max(selectedIndex - 1, 0);
            }

            const nextPlayer = displayData[nextIndex];
            if (!nextPlayer) return;

            setSelectedIndex(nextIndex);
            setPlayerID(nextPlayer.FBXID);
            setActivePlayer(nextPlayer);

            requestAnimationFrame(() => {
                const row = document.querySelector(`[data-rowindex="${nextIndex}"]`);
                row?.scrollIntoView({ block: 'nearest' });
            });
        };

        window.addEventListener('keydown', handleArrowNav);
        return () => window.removeEventListener('keydown', handleArrowNav);
    }, [activeSideButton, displayData, selectedIndex, setPlayerID, setActivePlayer, showMask]);

    const closeAddToBox = () => {
        setActivePlayer(null);
        setRapidListID(null);
    };

    const sort = (field) => {
        const order = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
        const sorted = sortPlayers(displayData, field, order);

        setDisplayData(sorted);
        setSortField(field);
        setSortOrder(order);
    };

    const isChecked = (FBXID) => quickListNumbers.includes(FBXID);

    const saveYLocation = (event, player, index) => {
        const row = event.target.closest('.nav-top-data');
        if (!row) return;

        const tappedPlayerID = String(player?.FBXID || '');
        const now = Date.now();
        const isDoubleTap =
            canOpenBluBars &&
            tappedPlayerID &&
            lastTapRef.current.playerID === tappedPlayerID &&
            now - lastTapRef.current.time <= DOUBLE_TAP_MS;
        lastTapRef.current = { playerID: tappedPlayerID, time: now };

        const rect = row.getBoundingClientRect();
        const boxWidth = 350;
        const offsetX = rect.left - 15;
        const newLeft = window.innerWidth - boxWidth - offsetX;

        setBoxPosition({ top: rect.y, left: newLeft });
        setSelectedIndex(index);

        setPlayerID(player.FBXID);
        setActivePlayer(player);

        if (typeof updateWhichOneLine === 'function') {
            updateWhichOneLine('One');
        }

        if (isAddOpen) {
            setRapidListID(player.FBXID);
        }

        if (isDoubleTap) {
            updateActiveSideButton('Blue');
        }
    };

    return (
        <div className={`one-liner-container ${showScrollbars ? "show-scrollbars" : ""}`}>
            <div className="nav-top">
                <div className="col-1 text-center">
                    <img
                        title="Add/Remove QuickList"
                        src={checkIconOff}
                        alt="Checkbox Icon"
                        style={{ width: '24px', height: '24px' }}
                    />
                </div>

                <div
                    title="Lastname, Firstname"
                    onClick={() => sort('last')}
                    className={`col-6 ps-1 ${sortField === 'last' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Name
                </div>

                <div
                    title="School"
                    onClick={() => sort('school')}
                    className={`text-center col-2 ${sortField === 'school' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    SCH
                </div>

                <div
                    title="General Position"
                    onClick={() => sort('genPos')}
                    className={`text-center col-1 ${sortField === 'genPos' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    CGP
                </div>

                <div
                    title="College Jersey"
                    onClick={() => sort('jersey')}
                    className={`text-center col-1 ${sortField === 'jersey' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    C#
                </div>

                <div
                    title="Pro Jersey"
                    onClick={() => sort('proJersey')}
                    className={`text-center col-1 ${sortField === 'proJersey' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    P#
                </div>

                <div
                    title="Height (Verified Code)"
                    onClick={() => sort('height')}
                    className={`text-center col-2 ${sortField === 'height' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    HT(V)
                </div>

                <div
                    title="Weight (Verified Code)"
                    onClick={() => sort('weight')}
                    className={`text-center col-2 ${sortField === 'weight' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    WT(V)
                </div>

                <div
                    title="Speed (Verified Code)"
                    onClick={() => sort('speed40Fastest')}
                    className={`text-center col-2 ${sortField === 'speed40Fastest' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    SP(V)
                </div>

                <div
                    title="CFL Team / Status"
                    onClick={() => sort('cflTeam')}
                    className={`text-center col-2 ${sortField === 'cflTeam' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    CFL
                </div>

                <div
                    title="CFL Transaction Date"
                    onClick={() => sort('cflDate')}
                    className={`text-center col-3 ${sortField === 'cflDate' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    CFL-Date
                </div>

                <div
                    title="CFL Draft Year / Round"
                    onClick={() => sort('cflData')}
                    className={`text-center col-3 ${sortField === 'cflData' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    CFL-Y/RD
                </div>

                <div
                    title="CFL Final Contract Year"
                    onClick={() => sort('optionYr')}
                    className={`text-center col-2 ${sortField === 'optionYr' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    FCY
                </div>

                <div
                    title="NFL Team / Status"
                    onClick={() => sort('nflTeam')}
                    className={`text-center col-2 ${sortField === 'nflTeam' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    NFL
                </div>

                <div
                    title="NFL Transaction Date"
                    onClick={() => sort('nflDate')}
                    className={`text-center col-3 ${sortField === 'nflDate' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    NFL-Date
                </div>

                <div
                    title="NFL Draft Year / Round"
                    onClick={() => sort('nflData')}
                    className={`text-center col-3 ${sortField === 'nflData' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    NFL-Y/RD
                </div>

                <div
                    title="UFL24 Team / Status"
                    onClick={() => sort('ufl24Team')}
                    className={`text-center col-3 ${sortField === 'ufl24Team' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    UFL 24
                </div>

                <div
                    title="Attended Event"
                    onClick={() => sort('theEvent')}
                    className={`text-center col-3 ${sortField === 'theEvent' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Event
                </div>

                <div
                    title="Age"
                    onClick={() => sort('age')}
                    className={`text-center col-1 ${sortField === 'age' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Age
                </div>

                <div
                    title="A = Has Agent"
                    onClick={() => sort('agentNum')}
                    className={`text-center col-1 ${sortField === 'agentNum' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    Agt.
                </div>

                <div
                    title="Highest Grade for Team"
                    onClick={() => sort('masterGrade')}
                    className={`text-center col-1 ${sortField === 'masterGrade' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    HG
                </div>

                <div
                    title="Reports/Notes/Files"
                    onClick={() => sort('report')}
                    className={`text-center col-2 ${sortField === 'report' ? (sortOrder === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    RNF
                </div>
            </div>

            <div className="scrollable-content">
                {displayData.map((player, index) => (
                    <div
                        className={`nav-top-data ${index === selectedIndex ? 'selected-player' : ''}`}
                        key={index}
                        data-rowindex={index}
                        onClick={(e) => saveYLocation(e, player, index)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div
                            className="col-1 text-center"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange(player.FBXID);
                            }}
                        >
                            <img
                                src={isChecked(player.FBXID) ? checkIcon : checkIconOff}
                                alt="Checkbox Icon"
                                style={{ width: '24px', height: '24px' }}
                            />
                        </div>

                        <div
                            className={`col-6 ps-1 ${player.impnip === 'G'
                                ? 'globalPlayer'
                                : player.impnip?.startsWith('V')
                                    ? 'brownPlayer'
                                    : ''
                                }`}
                            style={{
                                color: player.impnip === 'N' ? 'red' : '',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {player.last?.toUpperCase()}, {player.first?.toUpperCase()} {player.impnip !== '' ? '[' + player.impnip + ']' : ''}
                        </div>

                        <div
                            title={`${player.longSchool || ''} ${player.nickname || ''}`}
                            className="text-center col-2"
                        >
                            {player.school}
                        </div>

                        <div className="text-center col-1">{player.genPos}</div>
                        <div className="text-center col-1">
                            {player.jersey === '0' ? '' : player.jersey === '-1' ? '0' : player.jersey}
                        </div>
                        <div className="text-center col-1">
                            {player.proJersey === '0' ? '' : player.proJersey === '-1' ? '0' : player.proJersey}
                        </div>

                        <div
                            title={[
                                player.height || "-",
                                player.heightV || "",
                                player.heightDate && player.heightDate !== "0000-00-00" ? player.heightDate : "-"
                            ].filter(Boolean).join(" ")}
                            className="text-center col-2"
                        >
                            {[player.height, player.heightV].filter(Boolean).join(" ")}
                        </div>

                        <div
                            title={[
                                player.weight || "-",
                                player.weightV || "",
                                player.weightDate && player.weightDate !== "0000-00-00" ? player.weightDate : "-"
                            ].filter(Boolean).join(" ")}
                            className="text-center col-2"
                        >
                            {[player.weight, player.weightV].filter(Boolean).join(" ")}
                        </div>

                        <div
                            title={[
                                player.speed40Fastest || "-",
                                player.speed40FVerified || "",
                                player.speed40FDate && player.speed40FDate !== "0000-00-00" ? player.speed40FDate : "-"
                            ].filter(Boolean).join(" ")}
                            className="text-center col-2"
                        >
                            {[player.speed40Fastest, player.speed40FVerified].filter(Boolean).join(" ")}
                        </div>

                        <div
                            className={`text-center col-2 ${player.cflTeam && player.cflTeam !== 'null' ? 'cfl' : ''}`}
                            title={
                                player.cflTeam && player.cflTeam !== 'null'
                                    ? `${FindCFLTeam('cfl', player.cflTeam) || 'Unknown Team'} - ${player.cflPos || ''} - ${FindStatus('cfl', player.cflStatus) || ''} - ${player.cflDate || ''}`.trim()
                                    : ''
                            }
                        >
                            {player.cflTeam && player.cflTeam !== 'null' ? player.cflTeam : ''} {player.cflStatus}
                        </div>

                        <div className={`text-center col-3 ${player.cflDate && player.cflDate !== '-' ? 'cfl' : ''}`}>
                            {player.cflDate}
                        </div>

                        <div className={`text-center col-3 ${player.cflDraftYear ? 'cfl' : ''}`}>
                            {player.cflDraftYear || ''} {player.cflDraftRound || ''}
                        </div>

                        <div className={`text-center col-2 ${player.optionYr ? 'cfl' : ''}`}>
                            {player.optionYr}
                        </div>

                        <div
                            className={`text-center col-2 ${player.nflTeam && player.nflTeam !== 'null' ? 'nfl' : ''}`}
                            title={
                                player.nflTeam && player.nflTeam !== 'null' && player.nflTeam !== null
                                    ? `${FindCFLTeam('nfl', player.nflTeam) || 'Unknown Team'} - ${player.nflPos || ''} - ${FindStatus('nfl', player.nflStatus) || ''} - ${player.nflDate || ''}`.trim()
                                    : ''
                            }
                        >
                            {player.nflTeam && player.nflTeam !== 'null' ? player.nflTeam : ''} {player.nflStatus}
                        </div>

                        <div className={`text-center col-3 ${player.nflDate && player.nflDate !== '-' ? 'nfl' : ''}`}>
                            {player.nflDate}
                        </div>

                        <div className={`text-center col-3 ${player.nflDraftYear ? 'nfl' : ''}`}>
                            {player.nflDraftYear || ''} {player.nflDraftRound || ''}
                        </div>

                        <div
                            className={`text-center col-3 ${player.ufl24Team && player.ufl24Team !== 'null' ? 'ufl24' : ''}`}
                            title={
                                player.ufl24Team && player.ufl24Team !== 'null' && player.ufl24Team !== null
                                    ? `${FindCFLTeam('ufl', player.ufl24Team) || 'Unknown Team'} - ${player.ufl24Pos || ''} - ${FindStatus('ufl', player.ufl24Status) || ''} - ${player.ufl24Date || ''}`.trim()
                                    : ''
                            }
                        >
                            {player.ufl24Team && player.ufl24Team !== 'null' ? player.ufl24Team : ''} {player.ufl24Status}
                        </div>

                        <div
                            className={`text-center col-3 ${player.theEvent && player.theEvent !== 'null' ? 'event' : ''}`}
                            title={
                                player.theEvent && player.theEvent !== 'null' && player.theEvent !== null
                                    ? `${FindCFLTeam('event', player.theEvent) || 'Unknown Team'} - ${player.eventPos || ''} - ${player.theEventDate || ''} - ${player.eventSide || ''}`.trim()
                                    : ''
                            }
                        >
                            {player.theEvent !== 'null' ? player.theEvent : ''}
                        </div>

                        <div className="text-center col-1">{player.age}</div>
                        <div className="text-center col-1">{player.agentNum > 0 ? 'A' : ''}</div>
                        <div className="text-center col-1">{player.masterGrade}</div>
                        <div className="text-center col-2">{player.report} {player.note} {player.pdf}</div>
                    </div>
                ))}
            </div>

            {rapidListID && activePlayer && (
                <AddToBox
                    player={activePlayer}
                    style={{
                        position: 'absolute',
                        top: boxPosition.top,
                        left: boxPosition.left,
                        width: "350px",
                        zIndex: 1000,
                        height: "400px",
                    }}
                    onClose={closeAddToBox}
                />
            )}
        </div>
    );
};

export default OneLiner;
